import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, Square, X, Loader2, Video, MessageSquarePlus, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { feedbackApi } from '../services/api';

// Pick the first MediaRecorder mime type the browser actually supports.
const pickMime = (candidates) => {
  for (const c of candidates) {
    try { if (window.MediaRecorder && MediaRecorder.isTypeSupported(c)) return c; } catch (_) {}
  }
  return '';
};
const VIDEO_MIME = () => pickMime(['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']);
const AUDIO_MIME = () => pickMime(['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg']);

const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

/**
 * Floating voice/video feedback recorder. Mount once in the app shell (it renders a
 * fixed bottom-right launcher). Captures screen video + mic + shared tab/system audio,
 * minimizes to a pill while recording so the panel isn't in the capture, then uploads.
 * Opens on click or on the `acorns:record-feedback` window event.
 */
const FeedbackWidget = () => {
  const [phase, setPhase] = useState('idle'); // idle | panel | recording | uploading
  const [elapsed, setElapsed] = useState(0);

  const mediaRef = useRef({ recorders: [], streams: [], audioCtx: null });
  const videoChunks = useRef([]);
  const audioChunks = useRef([]);
  const trailRef = useRef([]);
  const startUrlRef = useRef('');
  const timerRef = useRef(null);
  const trailPollRef = useRef(null);
  const stoppingRef = useRef(false);

  const cleanupTimers = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (trailPollRef.current) { clearInterval(trailPollRef.current); trailPollRef.current = null; }
  };

  const stopAllTracks = () => {
    const m = mediaRef.current;
    (m.streams || []).forEach((s) => { try { s.getTracks().forEach((t) => t.stop()); } catch (_) {} });
    if (m.audioCtx) { try { m.audioCtx.close(); } catch (_) {} }
    mediaRef.current = { recorders: [], streams: [], audioCtx: null };
  };

  const resetAll = useCallback(() => {
    cleanupTimers();
    stopAllTracks();
    videoChunks.current = [];
    audioChunks.current = [];
    trailRef.current = [];
    stoppingRef.current = false;
    setElapsed(0);
    setPhase('idle');
  }, []);

  // Open the panel when any "Record feedback" trigger fires.
  useEffect(() => {
    const open = () => setPhase((p) => (p === 'idle' ? 'panel' : p));
    window.addEventListener('acorns:record-feedback', open);
    return () => window.removeEventListener('acorns:record-feedback', open);
  }, []);

  useEffect(() => () => { cleanupTimers(); stopAllTracks(); }, []);

  const pushTrail = (path) => {
    const arr = trailRef.current;
    if (arr[arr.length - 1] !== path) arr.push(path);
  };

  const doUpload = useCallback(async () => {
    setPhase('uploading');
    try {
      const videoBlob = videoChunks.current.length ? new Blob(videoChunks.current, { type: 'video/webm' }) : null;
      const audioBlob = audioChunks.current.length
        ? new Blob(audioChunks.current, { type: 'audio/webm' })
        : videoBlob; // fall back to the video blob if the audio-only track is empty

      if (!audioBlob) { toast.error('Nothing was recorded.'); resetAll(); return; }

      const fd = new FormData();
      fd.append('audio', audioBlob, 'audio.webm');
      if (videoBlob) fd.append('video', videoBlob, 'video.webm');
      fd.append('url', startUrlRef.current || window.location.href);
      fd.append('trail', JSON.stringify(trailRef.current || []));

      const { data } = await feedbackApi.create(fd);
      toast.success(`Feedback saved — ticket ${data?.ticket_no || ''}`, {
        description: 'Transcription & checklist are generating in the background.',
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Failed to upload feedback');
    } finally {
      resetAll();
    }
  }, [resetAll]);

  const handleStop = useCallback(() => {
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    cleanupTimers();
    const recs = mediaRef.current.recorders || [];
    let pending = recs.length;
    if (!pending) { doUpload(); return; }
    recs.forEach((r) => {
      const finish = () => { pending -= 1; if (pending <= 0) doUpload(); };
      try {
        if (r.state !== 'inactive') { r.onstop = finish; r.stop(); }
        else finish();
      } catch (_) { finish(); }
    });
    // stop the capture indicator promptly
    stopAllTracks();
  }, [doUpload]);

  const startRecording = useCallback(async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      toast.error('Screen recording is not supported in this browser.');
      return;
    }
    let screenStream;
    try {
      screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 12 },
        audio: true, // captures shared tab/system audio when the user ticks "Share audio"
      });
    } catch (_) {
      // user cancelled the picker
      return;
    }
    let micStream = null;
    try { micStream = await navigator.mediaDevices.getUserMedia({ audio: true }); } catch (_) { micStream = null; }

    // Mix mic + shared system audio into one stream (used both for the video soundtrack
    // and for the compact audio file the backend transcribes).
    let mixedAudio = null;
    let audioCtx = null;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      audioCtx = new AC();
      const dest = audioCtx.createMediaStreamDestination();
      if (micStream && micStream.getAudioTracks().length) {
        audioCtx.createMediaStreamSource(micStream).connect(dest);
      }
      if (screenStream.getAudioTracks().length) {
        audioCtx.createMediaStreamSource(new MediaStream(screenStream.getAudioTracks())).connect(dest);
      }
      mixedAudio = dest.stream;
    } catch (_) {
      mixedAudio = micStream; // fall back to mic-only
    }

    const videoTracks = screenStream.getVideoTracks();
    const audioTracks = mixedAudio ? mixedAudio.getAudioTracks() : [];
    const videoStream = new MediaStream([...videoTracks, ...audioTracks]);

    const recorders = [];
    try {
      const vm = VIDEO_MIME();
      const vr = new MediaRecorder(videoStream, vm ? { mimeType: vm } : undefined);
      vr.ondataavailable = (e) => { if (e.data && e.data.size) videoChunks.current.push(e.data); };
      recorders.push(vr);
    } catch (e) {
      stopAllTracks();
      toast.error('Could not start the screen recorder.');
      return;
    }
    if (audioTracks.length) {
      try {
        const am = AUDIO_MIME();
        const ar = new MediaRecorder(new MediaStream(audioTracks), am ? { mimeType: am } : undefined);
        ar.ondataavailable = (e) => { if (e.data && e.data.size) audioChunks.current.push(e.data); };
        recorders.push(ar);
      } catch (_) { /* video-only; backend falls back to the video for transcription */ }
    }

    mediaRef.current = {
      recorders,
      streams: [screenStream, micStream].filter(Boolean),
      audioCtx,
    };
    videoChunks.current = [];
    audioChunks.current = [];
    trailRef.current = [];
    startUrlRef.current = window.location.href;
    stoppingRef.current = false;
    pushTrail(window.location.pathname);

    // End if the user stops sharing via the browser's own control.
    if (videoTracks[0]) videoTracks[0].addEventListener('ended', handleStop);

    recorders.forEach((r) => { try { r.start(1000); } catch (_) {} });
    setElapsed(0);
    setPhase('recording');
    timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    trailPollRef.current = setInterval(() => pushTrail(window.location.pathname), 1500);
  }, [handleStop]);

  // ----- render -----
  if (phase === 'idle') {
    return (
      <button
        onClick={() => setPhase('panel')}
        title="Record feedback"
        data-testid="feedback-launcher"
        className="group fixed bottom-5 right-5 z-[60] flex items-center rounded-full bg-[#3E3F96] hover:bg-[#2D2E7A] text-white shadow-lg p-3 opacity-70 hover:opacity-100 transition-all"
      >
        <MessageSquarePlus className="w-5 h-5 shrink-0" />
        {/* Label stays collapsed so the launcher doesn't cover page content; expands on hover/focus */}
        <span className="hidden sm:inline text-sm font-medium max-w-0 overflow-hidden whitespace-nowrap group-hover:max-w-[6rem] group-focus:max-w-[6rem] group-hover:ml-2 group-focus:ml-2 group-hover:mr-1 group-focus:mr-1 transition-all duration-200">
          Feedback
        </span>
      </button>
    );
  }

  if (phase === 'recording' || phase === 'uploading') {
    const uploading = phase === 'uploading';
    return (
      <div className="fixed bottom-5 right-5 z-[60] flex items-center gap-3 rounded-full bg-white border border-[#D8D9E8] shadow-lg pl-4 pr-2 py-2">
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 text-[#3E3F96] animate-spin" />
            <span className="text-sm font-medium text-[#1C1F1D]">Uploading…</span>
          </>
        ) : (
          <>
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
            </span>
            <span className="text-sm font-medium text-[#1C1F1D] tabular-nums">{fmtTime(elapsed)}</span>
            <button
              onClick={handleStop}
              data-testid="feedback-stop"
              className="flex items-center gap-1 rounded-full bg-red-500 hover:bg-red-600 text-white text-xs font-semibold px-3 py-1.5 transition-colors"
            >
              <Square className="w-3 h-3 fill-current" /> Stop
            </button>
          </>
        )}
      </div>
    );
  }

  // phase === 'panel'
  return (
    <div className="fixed bottom-5 right-5 z-[60] w-80 rounded-xl bg-white border border-[#D8D9E8] shadow-2xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-[#3E3F96] text-white">
        <div className="flex items-center gap-2">
          <Video className="w-4 h-4" />
          <span className="text-sm font-semibold">Record feedback</span>
        </div>
        <button onClick={() => setPhase('idle')} data-testid="feedback-close" className="p-1 hover:bg-white/15 rounded">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-4 space-y-3">
        <p className="text-sm text-[#5C635F]">
          Capture your screen and voice to report an issue or request a change. Speak through what's wrong and what you'd
          like — we'll turn it into a developer checklist automatically.
        </p>
        <ul className="text-xs text-[#5C635F] space-y-1.5">
          <li className="flex gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-[#3E3F96] mt-0.5 shrink-0" /> Pick the tab/window to record.</li>
          <li className="flex gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-[#3E3F96] mt-0.5 shrink-0" /> Tick <b>&nbsp;Share tab audio&nbsp;</b> to capture a call.</li>
          <li className="flex gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-[#3E3F96] mt-0.5 shrink-0" /> This panel hides while recording.</li>
        </ul>
        <button
          onClick={startRecording}
          data-testid="feedback-start"
          className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#3E3F96] hover:bg-[#2D2E7A] text-white text-sm font-medium py-2.5 transition-colors"
        >
          <Mic className="w-4 h-4" /> Start recording
        </button>
      </div>
    </div>
  );
};

export default FeedbackWidget;
